import { useContext } from "react";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import {
  ExchangeName,
  GetAuthUrlRequest,
  OpenConfirmModal,
  UsageType,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { UseQuotaModal } from "@shared/features/settings/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";

interface useQuickConnectFormProps {
  primaryExchange?: ExchangeName;
  trackAvoConnectClicked: () => void;
  isOnboarding?: boolean;
  openAuthURL: (url: string) => void;
  useQuotaModal: UseQuotaModal;
  openConfirmModal: OpenConfirmModal<any>;
  redirectToWebQuickConnect?: (args: GetAuthUrlRequest) => void;
}

const useQuickConnectForm = ({
  primaryExchange,
  trackAvoConnectClicked,
  isOnboarding,
  openAuthURL,
  useQuotaModal,
  openConfirmModal,
  redirectToWebQuickConnect,
}: useQuickConnectFormProps) => {
  const { noFuturesAccountMessageData, quickConnectAlreadyUsedMessageData } =
    useContext(QuickConnectContext) || {};
  const {
    exchangeToClientName,
    accountName,
    setTab,
    handleOnSubmit,
    accountNameError,
    selectedSubExchanges,
  } = useContext(AccountFormContext)!;
  const {
    quotaModal,
    handleTriggerClick,
    isLoading: isLoadingQuotaCheck,
  } = useQuotaModal({
    usageType: UsageType.CLIENT,
  });

  const [getAuthUrl, { isLoading: getAuthUrlLoading }] =
    sharedLib.sharedApi.configuration.useLazyGetAuthUrlQuery();

  const onClickConnectToExchange = () => {
    handleTriggerClick().then((passedQuota) => {
      if (passedQuota || !primaryExchange) return;
      if (isOnboarding) {
        trackAvoConnectClicked();
      }
      if (!exchangeToClientName || !accountName) return;
      const args = {
        nested_exchange: primaryExchange,
        exchange_to_client_name: exchangeToClientName,
        client_base_name: accountName,
      };
      if (redirectToWebQuickConnect) {
        redirectToWebQuickConnect(args);
      } else {
        getAuthUrl(args)
          .unwrap()
          .then((response) => {
            openAuthURL(response);
          });
      }
    });
  };

  const handleButtonClick = () => {
    if (noFuturesAccountMessageData?.show)
      return noFuturesAccountMessageData.onSubmit?.();
    else if (quickConnectAlreadyUsedMessageData?.show)
      return quickConnectAlreadyUsedMessageData.onSubmit?.();
    else return onClickConnectToExchange();
  };

  const onButtonClick = () => {
    handleOnSubmit(primaryExchange, handleButtonClick, openConfirmModal);
  };

  const getButtonLoading = () => {
    if (noFuturesAccountMessageData?.show)
      return noFuturesAccountMessageData.loading;
    else if (quickConnectAlreadyUsedMessageData?.show)
      return quickConnectAlreadyUsedMessageData.loading;
    else return getAuthUrlLoading || isLoadingQuotaCheck;
  };

  const onSecondaryButtonClick = () => {
    setTab(ConnectExchangeTabs.API_KEYS);
  };

  const primaryButtonData = {
    text: `Connect to ${primaryExchange}`,
    disabled: accountNameError || !selectedSubExchanges?.length,
    loading: getButtonLoading(),
    onClick: onButtonClick,
  };

  const secondaryButtonData = {
    text: `Connect to ${primaryExchange} with API keys instead`,
    onClick: onSecondaryButtonClick,
  };

  return {
    quotaModal,
    noFuturesAccountMessageData,
    quickConnectAlreadyUsedMessageData,
    primaryButtonData,
    secondaryButtonData,
  };
};

export default useQuickConnectForm;
